'use client'

import { useState } from 'react'
import Image, { type ImageProps } from 'next/image'
import {
  shouldOptimizeThumbnail,
  type ResolvedVideoThumbnail,
} from '@/components/content/videos/helpers'
import {
  setYoutubeThumbnailQuality,
  ytFallbackThumbnailUrl,
  type YoutubeThumbnailQuality,
} from '@/lib/youtube'

// `quality` shadows next/image's numeric quality prop on purpose — we don't use
// the optimization-pipeline quality (YouTube is unoptimized, other sources have
// no consumer requesting it), and intersecting our string union with next's
// numeric union would narrow the prop to `undefined`.
type VideoThumbnailImageProps = Omit<ImageProps, 'src' | 'onError' | 'quality'> & {
  thumbnail: ResolvedVideoThumbnail
  // YouTube quality tier — ignored for non-YouTube sources. Defaults to 'medium'.
  quality?: YoutubeThumbnailQuality
}

// Source-aware wrapper around next/image for video thumbnails.
//   - youtube: applies the chosen quality, one-shot onError swap to hqdefault.jpg
//   - override/local: lets next/image optimize via /_next/image
//   - mux: external CDN, skip optimization
// To reset the fallback state when the source URL changes (e.g. async-loaded
// thumbnail), pass a `key` prop derived from the URL.
export default function VideoThumbnailImage({
  thumbnail,
  quality = 'medium',
  ...rest
}: VideoThumbnailImageProps) {
  const [failed, setFailed] = useState(false)

  if (thumbnail.source === 'youtube') {
    const primarySrc = setYoutubeThumbnailQuality(thumbnail.url, quality)
    return (
      <Image
        {...rest}
        src={failed ? ytFallbackThumbnailUrl(primarySrc) : primarySrc}
        unoptimized
        onError={() => setFailed(true)}
      />
    )
  }

  return (
    <Image {...rest} src={thumbnail.url} unoptimized={!shouldOptimizeThumbnail(thumbnail.source)} />
  )
}
